import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { api, participant, formatApiErrorDetail } from "../lib/api";
import { Brand } from "../components/Brand";

const HERO = "https://images.unsplash.com/photo-1578894381163-e72c17f2d45f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) {
      toast.error("Please enter your name and access code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/participant/verify", { code, name });
      participant.set(data.code, data.name);
      toast.success(`Welcome, ${data.name}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center justify-between">
          <Brand />
          <button
            onClick={() => nav("/admin/login")}
            data-testid="admin-link"
            className="flex items-center gap-1.5 text-sm text-[#1E293B]/60 transition-colors hover:text-[#2563EB]"
          >
            <Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Admin
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto w-full max-w-md py-16"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB]">Global Competence Profile</div>
          <h1 className="mt-3 font-display text-4xl leading-tight tracking-tight text-[#1E293B] sm:text-5xl">
            Measure your growth across borders.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#1E293B]/70">
            Eight validated self-assessments — from cross-cultural communication to foreign language confidence —
            combined into one weighted development score.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">Your name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="name-input"
                placeholder="e.g. Maria Gonzalez"
                className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-[#1E293B] outline-none transition-colors focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">Access code</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                data-testid="code-input"
                placeholder="XXXX-XXXX"
                className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 font-mono tracking-widest text-[#1E293B] outline-none transition-colors focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enter-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-1 disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Enter assessments"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <p className="mt-4 text-xs text-[#1E293B]/50">
            One code per participant grants access to all eight sections. Codes are issued by your program administrator.
          </p>
        </motion.div>
        <div className="text-xs text-[#1E293B]/40">© {new Date().getFullYear()} Global Compass · Study Abroad Assessment Platform</div>
      </div>

      {/* Right: image */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Passport and travel map" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1E293B]/25" />
      </div>
    </div>
  );
}
